import {runInAction} from 'mobx';
import {HomepageDialog, homepageStore} from '../store/HomepageStore';
import {getPortlets, setHomepageData, removeGroupItem} from '../api';
import {DefaultGroupsIDs} from '../enums';
import {showFormClientSideMessage} from '../../Form/actions/messages';
import texts from '../texts';
import {createShortcutGroup, findKeyByItem, getGroupTitle, isGroupUnderLimit} from '../utils';
import {priority} from '@priority-software/priority-proto-api/lib';
import {MAX_GROUPS_COUNT} from '../constants';
import {toggleGroup, updateCollapsedGroupState} from './collapsedGroups';
import {getHomepageData} from './data';
import {monitorAction} from '../../../services/datadog';
import {HomepageMonitoring} from '../../../constants/monitoring';

export const showPortlets = async () => {
    if (!homepageStore.portlets.length) {
        const res = await getPortlets();
        homepageStore.setPortlets(res);
    }
    homepageStore.openDialog(HomepageDialog.addPortlet, true);
};

export const showTilesSearch = (activeGroup: string) => {
    homepageStore.setSelectedGroup(activeGroup);
    homepageStore.openDialog(HomepageDialog.addTile, true);
};

const scrollAndExpandGroup = async (groupName: string) => {
    if (homepageStore.collapsedGroups.includes(groupName)) {
        toggleGroup(groupName, false);
        await updateCollapsedGroupState(groupName, false);
    }
    homepageStore.setScrollToParams(groupName, 'end');
};

export const addItemToHomepage = async (exec: string, title = '', groupName?: string) => {
    const group = homepageStore.groups.get(groupName);

    const data = Array.from(homepageStore.groups.values())
        .map(group => {
            return [{exec: group.exec, title: group.title}].concat(group.items);
        })
        .flat();

    const groupStartIndex = data.findIndex(item => item.title === group?.title);
    const newItem = {exec, title};
    const setData = [...data];

    if (groupStartIndex > -1) {
        const groupEndIndex = groupStartIndex + group.items.length;
        setData.splice(groupEndIndex + 1, 0, newItem);
    } else {
        setData.push(newItem);
    }

    await setHomepageData(setData);

    await getHomepageData();

    if (!group && groupName === DefaultGroupsIDs.insights) {
        monitorAction(HomepageMonitoring.createGroup, {
            [HomepageMonitoring.groupsCount]: homepageStore.groups.size
        });
    }

    await scrollAndExpandGroup(groupName);
};

export const addPortlet = async (exec: string, title: string) => {
    if (!homepageStore.items[exec]) {
        await addItemToHomepage(exec, title, DefaultGroupsIDs.insights);
        homepageStore.closeDialog();
    } else {
        showFormClientSideMessage('', priority.netitems.shared.Message.Type.Error, {
            content: texts.widget_already_exists
        });
    }
};

export const moveTileToGroup = async (
    exec: string,
    fromGroupTitle: string,
    toGroupTitle: string
) => {
    const setData = Array.from(homepageStore.groups.values())
        .map(group => {
            let items = group.items;
            if (group.title === fromGroupTitle) {
                items = group.items.filter(item => item.exec !== exec);
            } else if (group.title === toGroupTitle) {
                items = group.items
                    .filter(item => item.exec !== exec) // local fix to prevent adding multiple instances of tiles - Safari bug (EP-39305)
                    .concat(homepageStore.items[exec]);
            }
            return [{exec: group.exec, title: group.title}].concat(items);
        })
        .flat()
        .map(item => ({exec: item.exec, title: item.title}));

    await setHomepageData(setData);

    await getHomepageData();

    await scrollAndExpandGroup(toGroupTitle);
};

export const addTile = async (exec: string, title: string, groupName: string) => {
    const groupExists = homepageStore.groups.has(groupName);
    const groupWithCurrentTile = homepageStore.items[exec]
        ? findKeyByItem(homepageStore.groups, exec)
        : undefined;

    if (!!groupWithCurrentTile) {
        if (groupWithCurrentTile === groupName) {
            showFormClientSideMessage('', priority.netitems.shared.Message.Type.Information, {
                content: texts.tile_exists_in_current_group_message.replace('{X}', title),
                confirmButtonText: texts.group_name_exists_dialog_confirm
            });
            return;
        }

        const content = texts.replace_tile_dialog_message
            .replace('X', title)
            .replace('Y', `${getGroupTitle(groupWithCurrentTile)}`)
            .replace('Z', `${getGroupTitle(groupName)}`);

        showFormClientSideMessage('', priority.netitems.shared.Message.Type.Warning, {
            title: texts.replace_tile_dialog,
            content: content,
            confirmButtonText: texts.replace_tile_dialog_confirm_label,
            onConfirm: async () => {
                await moveTileToGroup(exec, groupWithCurrentTile, groupName);
                monitorAction(HomepageMonitoring.addTile);
            },
            cancleButtonText: texts.btn_cancel
        });

        return;
    }
    if (groupExists) {
        await addItemToHomepage(exec, title, groupName);
        monitorAction(HomepageMonitoring.addTile);
    } else {
        console.error(`Group with title "${groupName}" not found`);
    }
};

export const removeItemFromHomepage = async (exec: string, title: string) => {
    const item = homepageStore.items[exec];
    const isLastPortlet =
        item?.type === 'p' &&
        homepageStore.groups.get(DefaultGroupsIDs.insights).items?.length === 1;

    if (isLastPortlet) {
        // if the last portlet in the group, remove the insights group
        await removeGroup(DefaultGroupsIDs.insights);
        return;
    }

    const res = await removeGroupItem(exec, title);

    if (res) {
        runInAction(() => {
            const updatedGroups = new Map(homepageStore.groups); // create a copy of the map

            Array.from(homepageStore.groups.values()).forEach(group => {
                const hasItem = group.items.some(item => item.exec === exec);

                if (hasItem) {
                    updatedGroups.set(group.title, {
                        ...group,
                        items: group.items.filter(item => item.exec !== exec)
                    });
                }

                homepageStore.groups = updatedGroups; // update the map with the new value
                delete homepageStore.items[exec]; // remove the item from the items dictionary
            });
        });
    }
};

export const removeGroup = async (uniqueGroupName: string) => {
    const hasGroup = homepageStore.groups.has(uniqueGroupName);

    if (hasGroup) {
        const group = homepageStore.groups.get(uniqueGroupName);
        const groupItems = group.items || [];

        const data = Array.from(homepageStore.groups.values())
            .map(group => {
                if (group.title === uniqueGroupName) {
                    return [];
                }
                return [{exec: group.exec, title: group.title}].concat(group.items);
            })
            .flat();

        if (homepageStore.collapsedGroups.includes(uniqueGroupName)) {
            updateCollapsedGroupState(uniqueGroupName, false); // no need to wait for a response
        }

        // Perform the async operation
        await setHomepageData(data);

        // Modify MobX state in a synchronous action
        runInAction(() => {
            // Clear the group items data in the items dictionary
            const itemsDictionary = {...homepageStore.items};
            groupItems.forEach(item => {
                delete itemsDictionary[item.exec];
            });

            homepageStore.items = itemsDictionary; // Set new items dictionary
            homepageStore.groups.delete(uniqueGroupName); // Remove the group from the groups map
            homepageStore.closeDialog();
        });
        monitorAction(HomepageMonitoring.deleteGroup, {
            [HomepageMonitoring.groupsCount]: homepageStore.groups.size
        });
    }
};

export const showAddToHomepageDialog = (data: {exec: string; title: string}) => {
    homepageStore.addedItemFromMenu = data;
    homepageStore.openDialog(HomepageDialog.addToHomePage, true);
};

export const closeAddToHomepageDialog = () => {
    homepageStore.closeDialog();
    homepageStore.addedItemFromMenu = null;
};

export const addTileFromMenu = async (exec: string, title: string) => {
    const groups = Array.from(homepageStore.groups.keys()).filter(
        group => group !== DefaultGroupsIDs.insights
    );

    if (groups.length === 0) {
        homepageStore.groups.set(DefaultGroupsIDs.shortcuts, createShortcutGroup());
    }

    if (groups.length <= 1) {
        await addTile(exec, title, groups[0] || DefaultGroupsIDs.shortcuts);
    } else {
        showAddToHomepageDialog({exec, title});
    }
};

export const openNewGroupDialog = () => {
    if (isGroupUnderLimit(homepageStore.groups)) {
        homepageStore.openDialog(HomepageDialog.addGroup, true);
    } else {
        showFormClientSideMessage('', priority.netitems.shared.Message.Type.Error, {
            title: texts.group_limit_msg.replace(' X ', ` ${MAX_GROUPS_COUNT} `)
        });
    }
};
